// ============================================================
// USE AGENTS HOOK
// File: src/hooks/useAgents.js
// ============================================================
// Shared hook for fetching user/agent data across components

import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { useOrganization } from './useOrganization'
import toast from 'react-hot-toast'

/**
 * Hook to fetch agents/users from the database
 * @param {Object} options - Configuration options
 * @param {string|string[]} options.role - Filter by role ('inspector', 'admin', or ['inspector', 'admin'])
 * @param {boolean} options.activeOnly - Only fetch active users (default: false)
 * @param {string} options.select - Fields to select (default: 'id, first_name, last_name')
 * @param {boolean} options.showErrorToast - Show toast on error (default: true)
 * @param {boolean} options.enabled - Enable/disable the query (default: true)
 * @returns {Object} { agents, loading, error, refetch }
 */
export function useAgents(options = {}) {
  const {
    role = null,
    activeOnly = false,
    select = 'id, first_name, last_name',
    showErrorToast = true,
    enabled = true
  } = options

  const { organization } = useOrganization()
  const [agents, setAgents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  async function fetchAgents() {
    // Don't fetch if disabled or organization not loaded yet
    if (!enabled || !organization) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      let query = supabase
        .from('users')
        .select(select)

      // Filter by organization (important for super_admins)
      if (organization?.id) {
        query = query.eq('organization_id', organization.id)
      }

      // Filter by role if specified
      if (role) {
        if (Array.isArray(role)) {
          query = query.in('role', role)
        } else {
          query = query.eq('role', role)
        }
      }

      // Filter by active status if requested
      if (activeOnly) {
        query = query.eq('active', true)
      }

      const { data, error: fetchError } = await query.order('first_name')

      if (fetchError) throw fetchError

      setAgents(data || [])
    } catch (err) {
      console.error('Error fetching agents:', err)
      setError(err)
      if (showErrorToast) {
        toast.error('Failed to load agents')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAgents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id, enabled])

  return {
    agents,
    loading,
    error,
    refetch: fetchAgents
  }
}
